import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Download, FileText, FilePlus2, Trash2, FolderOpen, RefreshCw, History, Package, FileBadge, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import LogoUpload from "../documents/LogoUpload";
import GenerateSuiteDialog from "../documents/GenerateSuiteDialog";
import AuditorShareCard from "../documents/AuditorShareCard";
import DocumentPreviewSheet from "../documents/DocumentPreviewSheet";

const FOLDER_ORDER = [
  "01 - Core Policies",
  "02 - HR & Workforce",
  "03 - Governance & Operations",
  "04 - Service Delivery",
  "05 - Registers",
  "06 - Module Specific",
  "07 - Audit Submission",
];

const STATUSES = [
  { key: "generated",           label: "Generated",           tone: "bg-slate-100 text-slate-700" },
  { key: "reviewed",            label: "Reviewed",            tone: "bg-sky-100 text-sky-800" },
  { key: "needs_customisation", label: "Needs customisation", tone: "bg-amber-100 text-amber-800" },
  { key: "approved",            label: "Approved",            tone: "bg-[#E6F4F1] text-[#1A7F6E]" },
  { key: "submitted",           label: "Submitted",           tone: "bg-[#0D1B2A] text-white" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

export default function LeadDocumentsTab({ leadId, hasAssessment, initialHasLogo }) {
  const [docs, setDocs] = useState([]);
  const [hasLogo, setHasLogo] = useState(!!initialHasLogo);
  const [loading, setLoading] = useState(true);
  const [openGenerate, setOpenGenerate] = useState(false);
  const [expandedTpl, setExpandedTpl] = useState({});   // template_id -> bool (show history)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/documents/leads/${leadId}/documents`);
      setDocs(r.data);
    } catch { toast.error("Failed to load documents"); }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  // Group latest docs by folder, keep history under each template
  const { latestByFolder, historyByTemplate, totals } = useMemo(() => {
    const latest = docs.filter((d) => d.is_latest !== false);
    const hist = {};
    for (const d of docs) {
      hist[d.template_id] = hist[d.template_id] || [];
      hist[d.template_id].push(d);
    }
    for (const k of Object.keys(hist)) hist[k].sort((a, b) => (b.generated_at || "").localeCompare(a.generated_at || ""));

    const byFolder = {};
    for (const d of latest) {
      const k = d.folder || "01 - Core Policies";
      byFolder[k] = byFolder[k] || [];
      byFolder[k].push(d);
    }
    const approved = latest.filter((d) => d.status === "approved" || d.status === "submitted").length;
    return { latestByFolder: byFolder, historyByTemplate: hist, totals: { latest: latest.length, approved } };
  }, [docs]);

  async function download(d) {
    try {
      const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  }

  async function downloadZip() {
    setBulkBusy(true);
    try {
      const r = await api.get(`/documents/leads/${leadId}/documents/zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "document_suite.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Zip failed"); }
    setBulkBusy(false);
  }

  async function regenerateAll() {
    if (!window.confirm("Regenerate every latest document with the current assessment data? Previous versions are kept in history.")) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/documents/leads/${leadId}/regenerate-all`);
      toast.success(`${r.data.ok} regenerated${r.data.errors ? `, ${r.data.errors} failed` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBulkBusy(false);
  }

  async function generateAuditSummary() {
    setBulkBusy(true);
    try {
      const r = await api.post(`/documents/leads/${leadId}/audit-summary`);
      toast.success(`Audit Ready Summary ${r.data.version} generated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBulkBusy(false);
  }

  async function regenerateOne(d) {
    try {
      await api.post(`/documents/${d.id}/regenerate`);
      toast.success(`${d.template_name} regenerated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Regen failed"); }
  }

  async function changeStatus(d, status) {
    try {
      await api.put(`/documents/${d.id}`, { status });
      load();
    } catch { toast.error("Failed to update status"); }
  }

  async function del(d) {
    if (!window.confirm(`Delete ${d.filename}?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  }

  return (
    <div className="mt-4 space-y-4" data-testid="lead-documents-tab">
      <LogoUpload leadId={leadId} hasLogo={hasLogo} onChange={setHasLogo} />

      {!hasAssessment ? (
        <div className="p-6 border border-dashed border-slate-200 rounded-md text-center">
          <p className="text-sm text-slate-500">
            Link this lead to an assessment first — the assessment's answers drive template selection and placeholder values.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#0D1B2A]" data-testid="suite-headline">
                Document suite {totals.latest > 0 ? `· ${totals.approved} of ${totals.latest} approved` : "· not yet generated"}
              </p>
              <p className="text-xs text-slate-500">
                Click Generate to auto-fill every applicable policy template with this client's data.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {totals.latest > 0 && (
                <>
                  <Button size="sm" variant="outline" onClick={generateAuditSummary} disabled={bulkBusy} data-testid="audit-summary-btn">
                    <FileBadge className="w-3.5 h-3.5 mr-1.5" />Audit summary PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadZip} disabled={bulkBusy} data-testid="download-zip-btn">
                    <Package className="w-3.5 h-3.5 mr-1.5" />Download all (.zip)
                  </Button>
                  <Button size="sm" variant="outline" onClick={regenerateAll} disabled={bulkBusy} data-testid="regenerate-all-btn">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${bulkBusy ? "animate-spin" : ""}`} />Regenerate all
                  </Button>
                </>
              )}
              <Button onClick={() => setOpenGenerate(true)} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="open-generate-suite-btn">
                <FilePlus2 className="w-4 h-4 mr-2" />Generate suite
              </Button>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : totals.latest === 0 ? (
            <div className="p-6 border border-dashed border-slate-200 rounded-md text-center">
              <FileText className="w-8 h-8 mx-auto text-slate-300" />
              <p className="text-sm text-slate-500 mt-2">No documents generated yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {FOLDER_ORDER.filter((f) => latestByFolder[f]?.length).map((folder) => (
                <div key={folder} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`folder-${folder}`}>
                  <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-[#1A7F6E]" />
                    <p className="text-sm font-semibold text-[#0D1B2A]">{folder}</p>
                    <Badge variant="outline" className="ml-auto text-xs">{latestByFolder[folder].length}</Badge>
                  </div>
                  <ul>
                    {latestByFolder[folder].map((d) => (
                      <DocRow
                        key={d.id}
                        d={d}
                        expanded={!!expandedTpl[d.template_id]}
                        history={historyByTemplate[d.template_id] || []}
                        onDownload={download}
                        onPreview={setPreviewDoc}
                        onRegen={regenerateOne}
                        onDelete={del}
                        onStatus={changeStatus}
                        onToggleHistory={() => setExpandedTpl((s) => ({ ...s, [d.template_id]: !s[d.template_id] }))}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <GenerateSuiteDialog open={openGenerate} onOpenChange={setOpenGenerate} leadId={leadId} onGenerated={load} />

      {hasAssessment && <AuditorShareCard leadId={leadId} />}

      <DocumentPreviewSheet doc={previewDoc} open={!!previewDoc} onOpenChange={(v) => !v && setPreviewDoc(null)} />
    </div>
  );
}

function DocRow({ d, expanded, history, onDownload, onPreview, onRegen, onDelete, onStatus, onToggleHistory }) {
  const s = STATUS_MAP[d.status] || STATUS_MAP.generated;
  const olderVersions = history.filter((h) => h.id !== d.id);
  return (
    <>
      <li className="p-3 border-b last:border-b-0 border-slate-100 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`doc-${d.id}`}>
        <FileText className="w-4 h-4 text-slate-500 shrink-0" />
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onPreview(d)} data-testid={`preview-open-${d.id}`}>
          <p className="text-sm text-[#0D1B2A] truncate hover:text-[#1A7F6E] hover:underline">{d.template_name}</p>
          <p className="text-[11px] text-slate-500 flex flex-wrap items-center gap-1.5">
            <span className="font-medium">{d.version}</span>
            <span>·</span>
            <span>{(d.size_bytes / 1024).toFixed(0)} KB</span>
            <span>·</span>
            <span>{formatDate(d.generated_at)}</span>
            {olderVersions.length > 0 && (
              <>
                <span>·</span>
                <button onClick={(e) => { e.stopPropagation(); onToggleHistory(); }} className="text-[#1A7F6E] hover:underline inline-flex items-center gap-0.5" data-testid={`toggle-history-${d.id}`}>
                  <History className="w-3 h-3" />{olderVersions.length} older
                </button>
              </>
            )}
          </p>
        </div>
        <Select value={d.status || "generated"} onValueChange={(v) => onStatus(d, v)}>
          <SelectTrigger className={`h-7 text-xs w-40 border-none ${s.tone}`} data-testid={`status-select-${d.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={() => onPreview(d)} title="Preview" data-testid={`preview-doc-${d.id}`}>
            <Eye className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDownload(d)} data-testid={`dl-doc-${d.id}`}>
            <Download className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onRegen(d)} title="Regenerate this document" data-testid={`regen-doc-${d.id}`}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDelete(d)} className="text-rose-600 hover:text-rose-700" data-testid={`del-doc-${d.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </li>
      {expanded && olderVersions.map((h) => (
        <li key={h.id} className="pl-10 py-2 pr-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2 text-xs" data-testid={`history-${h.id}`}>
          <History className="w-3.5 h-3.5 text-slate-400" />
          <span className="flex-1 truncate text-slate-600">{h.version} · {(h.size_bytes / 1024).toFixed(0)} KB · {formatDate(h.generated_at)}</span>
          <Button size="sm" variant="ghost" onClick={() => onDownload(h)} className="h-6 px-2 text-xs" data-testid={`dl-hist-${h.id}`}>
            <Download className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(h)} className="h-6 px-2 text-xs text-rose-600 hover:text-rose-700" data-testid={`del-hist-${h.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </li>
      ))}
    </>
  );
}
